import { Observable, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';

export interface RetryConfig {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  shouldRetry?: (error: { status?: number; headers?: { get: (key: string) => string | null } }) => boolean;
}

/**
 * RxJS operator that implements exponential backoff retry strategy.
 *
 * @example
 * ```typescript
 * return this.http.get('/api/data').pipe(
 *   retryWithBackoff({
 *     maxRetries: 3,
 *     initialDelay: 1000,
 *     maxDelay: 30000,
 *     shouldRetry: (error) => error.status === 429
 *   })
 * );
 * ```
 *
 * Retry delays:
 * - 1st retry: 1s  (initialDelay × 2^0)
 * - 2nd retry: 2s  (initialDelay × 2^1)
 * - 3rd retry: 4s  (initialDelay × 2^2)
 * - 4th retry: 8s  (initialDelay × 2^3)
 *
 * If server provides Retry-After header, it will be used instead of calculated delay.
 */
export function retryWithBackoff(config: RetryConfig = {}) {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 30000,
    shouldRetry = (error) => error.status === 429 || (error.status !== undefined && error.status >= 500),
  } = config;

  return <T>(source: Observable<T>) =>
    source.pipe(
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error, index) => {
            const retryAttempt = index + 1;

            // Don't retry if max retries exceeded
            if (retryAttempt > maxRetries) {
              return throwError(() => error);
            }

            // Only retry specific errors
            if (!shouldRetry(error)) {
              return throwError(() => error);
            }

            // Calculate delay with exponential backoff
            const exponentialDelay = Math.min(
              initialDelay * Math.pow(2, index),
              maxDelay
            );

            // Add jitter to prevent thundering herd (random 0-1000ms)
            const jitter = Math.random() * 1000;
            const delayWithJitter = Math.min(
              exponentialDelay + jitter,
              maxDelay
            );

            // If server provides Retry-After header, use it
            const retryAfter = error.headers?.get('Retry-After');
            const finalDelay = retryAfter
              ? parseInt(retryAfter, 10) * 1000
              : delayWithJitter;

            // Wait for the calculated delay, then retry
            return timer(finalDelay);
          })
        )
      )
    );
}
